import {capitalize} from "@mui/material";
import {FormikConfig, FormikHelpers} from "formik";
import {useUpdateScheduler} from "../../client/hooks";
import {useCallback, useMemo} from "react";
import {Scheduler, SchedulerDaten} from "../../api";
import {useSchedulersHandling} from "./useSchedulersHandling";
import {useSchedulerFormSchema} from "./useSchedulerFormSchema";
import {useSnackbar} from "notistack";
import {useMutation, useQueryClient} from "react-query";
import type {SchedulerFormValues} from "../../types/schedulerTypes";

export function useSchedulerUpdateForm(scheduler: Scheduler): FormikConfig<SchedulerFormValues> {
    const {translation} = useSchedulersHandling();
    const {enqueueSnackbar} = useSnackbar();
    const {mutateAsync} = useSchedulerUpdate(scheduler.id);

    const schema = useSchedulerFormSchema();

    const initialTouched = useMemo(() => ({
        name: true,
        scope: true,
        mandantId: true,
    }), []);

    const initialValues: SchedulerFormValues = useMemo(() => ({
        name: scheduler.name,
        scope: scheduler.scope,
        mandantId: scheduler.mandantId ?? undefined,
        aufgabeId: scheduler.aufgabeId ?? undefined,
        timeSlots: scheduler.timeSlots,
        scheduleOnFreedays: scheduler.scheduleOnFreedays ?? false,
        scheduleOncePerTimeSlot: scheduler.scheduleOncePerTimeSlot ?? false,
        equidistantSchedulingEnabled: scheduler.equidistantSchedulingEnabled ?? false,
        equidistantSchedulingSeconds: scheduler.equidistantSchedulingSeconds ?? undefined,
        callOutsideTimeSlots: scheduler.callOutsideTimeSlots ?? false,
        catchUpInNextWindow: scheduler.catchUpInNextWindow ?? true,
        reschedulingStrategies: scheduler.reschedulingStrategies,
        enableRandomDistribution: scheduler.enableRandomDistribution ?? false,
        maxConcurrentCalls: scheduler.maxConcurrentCalls ?? undefined,
    }), [scheduler]);

    const onSubmitUpdateForm = useCallback(async (
        daten: SchedulerFormValues,
        {setSubmitting, setStatus}: FormikHelpers<SchedulerFormValues>
    ): Promise<void> => {
        try {
            await mutateAsync({
                name: daten.name,
                scope: daten.scope,
                mandantId: daten.scope !== 'GLOBAL' ? daten.mandantId : undefined,
                aufgabeId: daten.scope === 'AUFGABE' ? daten.aufgabeId : undefined,
                timeSlots: daten.timeSlots,
                scheduleOnFreedays: daten.scheduleOnFreedays,
                scheduleOncePerTimeSlot: daten.scheduleOncePerTimeSlot,
                equidistantSchedulingEnabled: daten.equidistantSchedulingEnabled,
                equidistantSchedulingSeconds: daten.equidistantSchedulingSeconds,
                callOutsideTimeSlots: daten.callOutsideTimeSlots,
                catchUpInNextWindow: daten.catchUpInNextWindow,
                reschedulingStrategies: daten.reschedulingStrategies,
                enableRandomDistribution: daten.enableRandomDistribution,
                maxConcurrentCalls: daten.maxConcurrentCalls,
            });
            setStatus(undefined);
            enqueueSnackbar(capitalize(translation('scheduler-update-successful')), {
                variant: "success",
            });
        } catch (error: unknown) {
            enqueueSnackbar(capitalize(translation('scheduler-update-failed')), {
                variant: "error",
            });
            if (error instanceof Error) {
                setStatus(error.message);
            } else {
                setStatus("An unknown error occurred.");
            }
        } finally {
            setSubmitting(false);
        }
    }, [mutateAsync, enqueueSnackbar, translation]);

    return useMemo(
        () => ({
            validationSchema: schema,
            validateOnMount: true,
            enableReinitialize: true,
            initialTouched: initialTouched,
            initialValues: initialValues,
            onSubmit: onSubmitUpdateForm
        }),
        [schema, initialValues, onSubmitUpdateForm, initialTouched]
    );
}

function useSchedulerUpdate(schedulerId: string) {
    const updateScheduler = useUpdateScheduler();
    const queryClient = useQueryClient();

    return useMutation(
        (input: SchedulerDaten): Promise<Scheduler> => {
            return updateScheduler(schedulerId, input);
        },
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(["schedulers", schedulerId]);
            },
        }
    );
}
